"use client";

import RichText from "@app/_blocks/RichText";
import useTranslate from "@app/_hooks/useTranslate";
import BoxIcon from "@app/_icons/BoxIcon/BoxIcon";
import CheckIconLarge from "@app/_icons/CheckIconLarge/CheckIconLarge";
import { useCart } from "@features/cart/hooks/useCart";
import type { Market } from "@features/market/config/i18n-config";
import type { ProductPageInfoBlockFragment } from "@server/gql/graphql";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@ui/dialog";
import { XIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

type ProductFreeShippingProps = {
  usp: ProductPageInfoBlockFragment;
  market: Market;
  price: number;
} & ComponentProps<"li">;

export default function ProductFreeShipping({
  usp,
  market,
  price,
  className,
  ...props
}: ProductFreeShippingProps) {
  const t = useTranslate();
  const { cart } = useCart();
  const cartTotal = cart?.totalGrossAmount ?? 0;
  const freeShippingThreshold = 1000;
  const hasReached = cartTotal >= freeShippingThreshold;
  const willReach = cartTotal + price > freeShippingThreshold && !hasReached;

  let message: ReactNode = usp.title;
  if (hasReached) {
    message = t("hasReachedFreeShippingMessage");
  } else if (willReach) {
    message = t("freeShippingMessage");
  }

  return (
    <li className={className} {...props}>
      <Dialog>
        <DialogTrigger className="flex cursor-pointer list-none items-start gap-4 p-4 text-left whitespace-break-spaces [&::-webkit-details-marker]:hidden">
          <span className="relative size-5 overflow-hidden">
            {hasReached || willReach ? (
              <CheckIconLarge className="size-5 shrink-0 text-green-500" />
            ) : (
              <BoxIcon className="size-5 shrink-0 text-black" />
            )}
          </span>
          {message}
        </DialogTrigger>
        <DialogContent className="p-8">
          <DialogHeader className="sticky top-0 z-10 h-0 min-h-0 p-0">
            <DialogTitle className="sr-only">{usp.title}</DialogTitle>
            <DialogClose className="absolute top-0 right-0">
              <XIcon className="size-5 shrink-0 text-black" />
            </DialogClose>
          </DialogHeader>
          <RichText
            content={usp.content}
            market={market.countryCode}
            locale={market.siteLocale}
          />
        </DialogContent>
      </Dialog>
    </li>
  );
}
